import { useMemo, useState } from "react";
import type { ColumnFiltersState, PaginationState } from "@tanstack/react-table";
import { Plus } from "lucide-react";

import { pageCount } from "@/core/api/pagination";
import { FLASHPAY_ROLES, useRealmRoles } from "@/core/auth";
import { DataTableClar } from "@/core/components/shared/data-table";
import { Button } from "@/core/components/ui/button";
import {
  ReportingGaugesDrawer,
  reportingEndpoints,
  useReportingGauges,
} from "@/features/reporting";
import { useMerchants } from "@/features/merchant/api/use-merchants";
import type { MerchantFilter } from "@/features/merchant/api/dto";
import {
  MerchantFormDialog,
  merchantsColumns,
  type MerchantRow,
} from "@/features/merchant/components";
import { useMerchantTranslation } from "@/features/merchant/i18n/use-merchant-translation";

const GAUGE_ROLES = [
  FLASHPAY_ROLES.SUPERADMIN,
  FLASHPAY_ROLES.ADMIN,
  FLASHPAY_ROLES.SERVICE,
] as const;

const MERCHANT_SETTLEMENT_GAUGE = [
  "flashpay_merchant_active_without_settlement_wallet",
] as const;

/** Traduit les filtres DataTableClar en `MerchantFilter` (§4.2). */
const toMerchantFilter = (filters: ColumnFiltersState): MerchantFilter => {
  const filter: MerchantFilter = {};
  for (const { id, value } of filters) {
    if (typeof value !== "string" || !value) continue;
    if (id === "status") filter.status = value as MerchantFilter["status"];
    if (id === "merchantType")
      filter.merchantType = value as MerchantFilter["merchantType"];
    if (id === "ownerPartyId") filter.ownerPartyId = value;
    if (id === "tradeName") filter.tradeName = value;
  }
  return filter;
};

/**
 * Liste paginée des marchands (`POST /Merchants/all`) — file de travail
 * back-office. Filtres select sur `status` et `merchantType`, texte sur
 * `tradeName` et `ownerPartyId` (champs du `MerchantFilter`).
 */
export function MerchantsPage() {
  const { t: tm } = useMerchantTranslation();
  const realmRoles = useRealmRoles();
  const canGauges = GAUGE_ROLES.some((role) => realmRoles.includes(role));
  const merchantGauges = useReportingGauges("merchant", canGauges);
  const [createOpen, setCreateOpen] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const params = useMemo(
    () => ({
      filter: toMerchantFilter(columnFilters),
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    }),
    [columnFilters, pagination]
  );

  const { data, isLoading, isFetching, error, refetch } = useMerchants(params);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold text-foreground">
          {tm("merchant.merchants.title")}
        </h1>
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {tm("merchant.merchants.create")}
          </Button>
          {canGauges ? (
            <ReportingGaugesDrawer
              gauges={merchantGauges.data ?? []}
              names={MERCHANT_SETTLEMENT_GAUGE}
              provenanceEndpoints={[reportingEndpoints.merchant.gauges]}
            />
          ) : null}
        </div>
      </div>

      <DataTableClar
        data={(data?.items ?? []).filter((m): m is MerchantRow => Boolean(m.id))}
        columnsClar={merchantsColumns}
        title={tm("merchant.merchants.title")}
        exportFileName="merchants"
        paginationMode="server"
        serverPageCount={data ? pageCount(data) : 0}
        totalItemsCount={data?.total ?? 0}
        pagination={pagination}
        onPaginationChange={setPagination}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        loading={{ isLoading, isFetching }}
        error={
          error
            ? { message: error.message, refetch: () => void refetch() }
            : undefined
        }
      />

      <MerchantFormDialog open={createOpen} onOpenChange={setCreateOpen} />
    </section>
  );
}
